/**
 * QuickCreate speed-dial: the pure decisions behind the floating "+" (owner
 * request 2026-10-06, supersedes Q-020b; DESIGN_SYSTEM.md §10.4). No React, no
 * DOM, so `npm run test:unit` covers it (src/utils/__tests__/speedDialModel.test.ts).
 *
 * The stack unfolds UPWARD from the button: DOM order = reading order = the
 * owner's order (משימה חדשה first), drawn bottom → top with
 * `flex-direction: column-reverse`, so the first item sits nearest the thumb.
 * The arrow keys follow what the eye sees: ↑ goes to the next item (up the
 * stack), ↓ to the previous one (down, towards the button).
 */
import { moveIndex, typeaheadIndex } from '../ui/overlayCore';

export type SpeedDialAction = 'task' | 'worklog' | 'ticket' | 'timer' | 'meeting';

/** Chip tint per kind (DESIGN_SYSTEM.md §2.8 families); `ink` = the emphasised item. */
export type SpeedDialTone = 'ink' | 'brand' | 'sky' | 'mint' | 'cyan';

const KIND_TONE: Record<SpeedDialAction, Exclude<SpeedDialTone, 'ink'>> = {
  task: 'brand', // work = brand blue
  ticket: 'brand',
  worklog: 'sky', // time = light blue
  timer: 'sky',
  meeting: 'cyan', // agenda / meetings = cyan
};

/**
 * The owner's order, bottom (nearest the button) → top. Clocking in / out left
 * the list for the top bar (owner, 2026-10-11).
 */
export const SPEED_DIAL_ORDER: readonly SpeedDialAction[] = ['task', 'worklog', 'ticket', 'timer', 'meeting'];

/**
 * The emphasised item (filled ink chip): the first item, which is the owner's
 * primary "משימה חדשה" whenever the role has it.
 */
export function speedDialTone(action: SpeedDialAction, index: number): SpeedDialTone {
  return index === 0 ? 'ink' : KIND_TONE[action];
}

// ─── Keyboard ────────────────────────────────────────────────────────────────

export type SpeedDialKeyResult =
  | { type: 'focus'; index: number }
  | { type: 'close'; refocus: true }
  | { type: 'none' };

/** What a key press inside the open stack does. `index` is the focused item. */
export function speedDialKey(key: string, index: number, labels: string[]): SpeedDialKeyResult {
  const disabled = labels.map(() => false);
  switch (key) {
    case 'ArrowUp': return { type: 'focus', index: moveIndex('next', index, disabled) };
    case 'ArrowDown': return { type: 'focus', index: moveIndex('prev', index, disabled) };
    case 'Home': return { type: 'focus', index: moveIndex('first', index, disabled) };
    case 'End': return { type: 'focus', index: moveIndex('last', index, disabled) };
    case 'Escape':
    case 'Tab':
      return { type: 'close', refocus: true };
    default: {
      if (key.length !== 1 || key === ' ') return { type: 'none' };
      const next = typeaheadIndex(labels, index, key, disabled);
      return next >= 0 ? { type: 'focus', index: next } : { type: 'none' };
    }
  }
}

// ─── Motion (DESIGN_SYSTEM.md §6) ────────────────────────────────────────────

/** Per-item transition, and the step between items. Enter and exit stay ≤ 220 ms in total. */
export const SPEED_DIAL_MOTION = {
  itemIn: 150,
  stepIn: 12,
  itemOut: 110,
  stepOut: 10,
  /** Hard ceiling for the whole stack (owner brief). */
  budget: 220,
} as const;

/**
 * Stagger delay of one item. Enter: from the button upward (index 0 first).
 * Exit: from the top down, so the stack folds back into the button along the
 * same path it came out.
 */
export function speedDialDelay(index: number, count: number, phase: 'enter' | 'exit'): number {
  const m = SPEED_DIAL_MOTION;
  if (phase === 'enter') return index * m.stepIn;
  return Math.max(0, count - 1 - index) * m.stepOut;
}

/** Total time of the stack's enter / exit for `count` items (the exit is how long it stays mounted). */
export function speedDialDuration(count: number, phase: 'enter' | 'exit'): number {
  const m = SPEED_DIAL_MOTION;
  const n = Math.max(1, count);
  return phase === 'enter' ? (n - 1) * m.stepIn + m.itemIn : (n - 1) * m.stepOut + m.itemOut;
}

// ─── Steps aside while scrolling (phones and touch) ─────────────────────────

/**
 * On a phone or any touch screen the floating button sits over the content, so
 * mid-scroll it can cover a row's own button. While the page scrolls DOWN it
 * steps aside (fades and shrinks, opacity + transform only); it comes back on a
 * scroll UP, near the top of the page, and when the scroll stops. Never while
 * its menu is open (the component does not track scroll then).
 */
export const FAB_TUCK = {
  /** Where it applies: touch input, or narrower than md (Tailwind `md` = 768 px). */
  media: '(pointer: coarse), (max-width: 767.98px)',
  /** Within this many px of the top it always shows. */
  topEdge: 24,
  /** A run of this many px in one direction flips it (ignores jitter and tiny nudges). */
  distance: 12,
  /** No scroll event for this long = the scroll stopped: show it again. */
  idleMs: 600,
} as const;

export interface FabScrollState {
  tucked: boolean;
  /** The scroll position the current run started from (the last turning point). */
  anchorY: number;
}

/** One scroll sample → the next state. Pure, so `npm run test:unit` covers it. */
export function fabScrollStep(state: FabScrollState, y: number): FabScrollState {
  if (y <= FAB_TUCK.topEdge) return { tucked: false, anchorY: y };
  const run = y - state.anchorY;
  if (!state.tucked) {
    if (run >= FAB_TUCK.distance) return { tucked: true, anchorY: y };
    // Moving up while shown: this is the new low point a downward run counts from.
    return run < 0 ? { tucked: false, anchorY: y } : state;
  }
  if (run <= -FAB_TUCK.distance) return { tucked: false, anchorY: y };
  // Still moving down while tucked: the upward run counts from the lowest point.
  return run > 0 ? { tucked: true, anchorY: y } : state;
}
